import { useCallback } from "react";
import { Node, Edge } from "reactflow";
import { message } from "antd";
import { 
  VariablePlaceholder,
  ToolCall
} from "../utils/types";
import { ModalState, DeleteModalState } from "../hooks/hooks";
import {
  debugLog,
  extractPlaceholders,
  getNodeChildren,
  getNextNodeId,
  resetNodeIdCounter,
  deduplicateEdges,
  resolveIdentifierToNodeId,
} from "../utils/utils";

interface UseNodeOperationsProps {
  nodes: Node[];
  edges: Edge[];
  setNodes: (nodes: Node[] | ((prev: Node[]) => Node[])) => void;
  setEdges: (edges: Edge[] | ((prev: Edge[]) => Edge[])) => void;
  modalState: ModalState;
  deleteModalState: DeleteModalState;
  placeholders: Record<string, VariablePlaceholder>;
  setPlaceholders: (placeholders: Record<string, VariablePlaceholder>) => void;
  saveToHistory: (actionType: "add" | "delete" | "paste") => void;
  identifiers: string[];
  setIdentifiers: (identifiers: string[] | ((prev: string[]) => string[])) => void;
  globalVariableRegistry: Record<string, VariablePlaceholder>;
  setGlobalVariableRegistry: (registry: Record<string, VariablePlaceholder> | ((prev: Record<string, VariablePlaceholder>) => Record<string, VariablePlaceholder>)) => void;
  isFirstNode: boolean;
  setIsFirstNode: (isFirstNode: boolean) => void;
  setShouldRepositionAfterNodeAddition: (should: boolean) => void;
  hasChildWithIdentifier: (parentId: string, identifier: string) => boolean;
  resetModalState: () => void;
  resetDeleteModalState: () => void;
  getNodeDescendants: (nodeId: string, allNodes: Node[], allEdges: Edge[]) => string[];
  initialNodes: Node[];
  initialEdges: Edge[];
  updateNodeHandlers: (currentEdges?: Edge[]) => void;
  setManuallyMovedNodes: React.Dispatch<React.SetStateAction<Set<string>>>;
  repositionTemporaryDecisionChildren: (decisionNodeId: string, retryCount?: number) => void;
  nodeDimensions: Record<string, number>;
}

export const useNodeOperations = ({
  nodes,
  edges,
  setNodes,
  setEdges,
  modalState,
  deleteModalState,
  placeholders,
  setPlaceholders,
  saveToHistory,
  identifiers,
  setIdentifiers,
  globalVariableRegistry,
  setGlobalVariableRegistry,
  isFirstNode,
  setIsFirstNode,
  setShouldRepositionAfterNodeAddition,
  hasChildWithIdentifier,
  resetModalState,
  resetDeleteModalState,
  getNodeDescendants,
  initialNodes,
  initialEdges,
  updateNodeHandlers,
  setManuallyMovedNodes,
  repositionTemporaryDecisionChildren,
  nodeDimensions,
}: UseNodeOperationsProps) => {

  // Style used for upstream (back-link/loop) connections
  const UPSTREAM_EDGE_STYLE = { stroke: "#1677ff", strokeDasharray: "2 6", strokeWidth: 2 } as const;

  // Determine if connecting source -> target would be upstream based on coordinates.
  // Primary rule: target meaningfully above source (lower y = higher on canvas) counts as upstream.
  // Secondary (tie) rule: if roughly same y, a strong left-backflow (target far left of source) also counts.
  // Fallback: if positions missing, use ancestry check.
  const isUpstreamConnection = useCallback((sourceId: string, targetId: string, allNodes: Node[], allEdges: Edge[]) => {
    try {
      const sourceNode = allNodes.find(n => n.id === sourceId);
      const targetNode = allNodes.find(n => n.id === targetId);
      if (sourceNode?.position && targetNode?.position) {
        const VERTICAL_THRESHOLD = 12; // px tolerance
        const HORIZONTAL_BACKFLOW_THRESHOLD = -120; // px to the left of source
        const dy = targetNode.position.y - sourceNode.position.y;
        const dx = targetNode.position.x - sourceNode.position.x;
        if (dy < -VERTICAL_THRESHOLD) return true; // clearly above
        if (Math.abs(dy) <= VERTICAL_THRESHOLD && dx < HORIZONTAL_BACKFLOW_THRESHOLD) return true; // same row but flowing back left
        return false;
      }
      // Fallback to topology if positions aren't available
      const descendantsOfTarget = getNodeDescendants(targetId, allNodes, allEdges);
      return descendantsOfTarget.includes(sourceId);
    } catch (_e) {
      return false;
    }
  }, [getNodeDescendants]);

  // Helper function to get node dimensions with fallbacks and collision avoidance
  const getNodeDimensions = useCallback((nodeId: string) => {
    const DEFAULT_NODE_WIDTH = 280;
    const DEFAULT_NODE_HEIGHT = 120;
    const MIN_HORIZONTAL_SPACING = 50;
    const MIN_VERTICAL_SPACING = 180;
    
    const height = nodeDimensions[nodeId] || DEFAULT_NODE_HEIGHT;
    // For width, we use the default since we don't track width changes
    const width = DEFAULT_NODE_WIDTH;
    
    // Calculate base spacing
    const baseHorizontalSpacing = Math.max(MIN_HORIZONTAL_SPACING, width * 0.2);
    const baseVerticalSpacing = Math.max(MIN_VERTICAL_SPACING, height + 60);
    
    // Get the current node to check its children count
    const currentNode = nodes.find(n => n.id === nodeId);
    const childCount = edges.filter(edge => edge.source === nodeId).length;
    
    // Increase spacing for nodes with many children to reduce edge crossings
    const childrenSpacingMultiplier = Math.min(1 + (childCount * 0.15), 2.0); // Cap at 2x spacing
    
    // Increase spacing based on tree density in the area
    const nearbyNodesCount = nodes.filter(node => {
      if (!node.position || node.id === nodeId) return false;
      const distance = Math.sqrt(
        Math.pow((currentNode?.position?.x || 0) - node.position.x, 2) + 
        Math.pow((currentNode?.position?.y || 0) - node.position.y, 2)
      );
      return distance < 400; // Count nodes within 400px radius
    }).length;
    
    const densitySpacingMultiplier = Math.min(1 + (nearbyNodesCount * 0.05), 1.5); // Cap at 1.5x spacing
    
    return {
      width,
      height,
      // Apply multipliers to reduce collisions
      horizontalSpacing: baseHorizontalSpacing * childrenSpacingMultiplier * densitySpacingMultiplier,
      verticalSpacing: baseVerticalSpacing * Math.max(childrenSpacingMultiplier, densitySpacingMultiplier)
    };
  }, [nodeDimensions, nodes, edges]);

  // Helper function to check if a node has children (is not a "free child")
  const nodeHasChildren = useCallback((nodeId: string) => {
    // Check regular edges
    const hasRegularChildren = edges.some(edge => edge.source === nodeId);
    
    // Check decision paths
    const node = nodes.find(n => n.id === nodeId);
    const hasDecisionChildren = node?.data.entityType === "DECISION" && 
      node.data.decisionData && 
      (node.data.decisionData.truePath || node.data.decisionData.falsePath);
    
    return hasRegularChildren || hasDecisionChildren;
  }, [nodes, edges]);

  // Smart positioning logic - reusable for both node creation and reorganization
  const calculateSmartPosition = useCallback((parentNode: Node, childNode: Node, allNodes: Node[], allEdges: Edge[]) => {
    const NODE_WIDTH = 280;
    const NODE_HEIGHT = 120;
    const HORIZONTAL_SPACING = 150;
    const VERTICAL_SPACING = 180;
    
    // Special positioning logic for decision nodes
    if (parentNode.data.entityType === "DECISION") {
      // Count existing decision paths
      const decisionData = parentNode.data.decisionData || { truePath: null, falsePath: null, conditions: [] };
      const existingPaths = [decisionData.truePath, decisionData.falsePath].filter(Boolean).length;
      
      // Simple positioning: first child goes left (-150px), second child goes right (+150px)
      let xOffset = -HORIZONTAL_SPACING; // Default to left (true path)
      if (existingPaths === 1) {
        // If there's already one path, this will be the second path (right side)
        xOffset = HORIZONTAL_SPACING;
      }
      
      return {
        x: parentNode.position.x + xOffset,
        y: parentNode.position.y + VERTICAL_SPACING
      };
    } else {
      // Regular node positioning logic with centering (for any non-DECISION node)
      const childNodeIds = getNodeChildren(parentNode.id, allEdges);
      const existingChildren = childNodeIds.map(id => allNodes.find(n => n.id === id)).filter(Boolean);
      
      // Calculate total number of children (including the one we're about to add)
      const totalChildren = existingChildren.length + 1;
      
      if (existingChildren.length === 0) {
        // First child - position directly below parent (will be centered)
        return {
          x: parentNode.position.x,
          y: parentNode.position.y + VERTICAL_SPACING
        };
      } else {
        // Multiple children - calculate centered positions
        const totalWidth = (totalChildren - 1) * (NODE_WIDTH + (HORIZONTAL_SPACING / 2));
        const startX = parentNode.position.x - (totalWidth / 2);
        const childIndex = existingChildren.length; // This will be the index of the new child
        
        return {
          x: startX + childIndex * (NODE_WIDTH + (HORIZONTAL_SPACING / 2)),
          y: parentNode.position.y + VERTICAL_SPACING
        };
      }
    }
  }, [getNodeChildren]);

  // Function to reposition all children of a parent to be centered
  const repositionChildrenCentered = useCallback((parentNodeId: string, allNodes: Node[], allEdges: Edge[], alreadyPositionedNodes?: Set<string>) => {
    // Get dynamic dimensions for the parent node
    const parentDimensions = getNodeDimensions(parentNodeId);
    
    const parentNode = allNodes.find(n => n.id === parentNodeId);
    if (!parentNode) {
      console.log(`[DEBUG] repositionChildrenCentered: Parent node ${parentNodeId} not found`);
      return allNodes;
    }
    console.log(`[DEBUG] repositionChildrenCentered called for ${parentNode.data.entityType} parent ${parentNodeId}`);
    if (parentNode.data.entityType === "DECISION") {
      console.log(`[DEBUG] repositionChildrenCentered: Skipping DECISION node ${parentNodeId} - decision nodes use special positioning`);
      return allNodes; // Skip decision nodes - they use special left/right positioning
    }
    
    const childNodeIds = getNodeChildren(parentNodeId, allEdges);
    const children = childNodeIds.map(id => allNodes.find(n => n.id === id)).filter(Boolean) as Node[];
    
    console.log(`[DEBUG] repositionChildrenCentered: Found ${children.length} children for parent ${parentNodeId}:`, children.map(c => c.id));
    
    if (children.length === 0) {
      console.log(`[DEBUG] repositionChildrenCentered: No children found, no repositioning needed`);
      return allNodes;
    }
    
    if (children.length === 1) {
      const child = children[0];
      
      // Check if the child has already been positioned during reorganization
      if (alreadyPositionedNodes && alreadyPositionedNodes.has(child.id)) {
        console.log(`[DEBUG] repositionChildrenCentered: Single child ${child.id} already positioned by another parent, skipping repositioning`);
        return allNodes; // Child has already been positioned, don't move it
      }
      
      // Single child with no other dependencies - position directly under parent
      console.log(`[DEBUG] repositionChildrenCentered: Positioning single child ${child.id} directly under parent`);
      
      // Mark this child as positioned
      if (alreadyPositionedNodes) {
        alreadyPositionedNodes.add(child.id);
      }
      
      return allNodes.map(node => {
        if (node.id === child.id) {
          const newX = parentNode.position.x;
          const newY = parentNode.position.y + parentDimensions.verticalSpacing;
          console.log(`[DEBUG] repositionChildrenCentered: Moving single child ${child.id} from (${node.position.x}, ${node.position.y}) to (${newX}, ${newY}) using dynamic spacing ${parentDimensions.verticalSpacing}`);
          return {
            ...node,
            position: { x: newX, y: newY }
          };
        }
        return node;
      });
    }
    
    // Calculate centered positions for multiple children using dynamic spacing
    
    // Calculate total width needed for all children with dynamic spacing
    const childSpacing = parentDimensions.width + parentDimensions.horizontalSpacing;
    const totalWidth = (children.length - 1) * childSpacing;
    const startX = parentNode.position.x - (totalWidth / 2);
    
    console.log(`[DEBUG] repositionChildrenCentered: Centering ${children.length} children with dynamic spacing`);
    console.log(`[DEBUG] repositionChildrenCentered: Parent at (${parentNode.position.x}, ${parentNode.position.y})`);
    console.log(`[DEBUG] repositionChildrenCentered: Child spacing: ${childSpacing}, Total width: ${totalWidth}, Start X: ${startX}`);
    
    // Update positions of all children
    return allNodes.map(node => {
      const childIndex = children.findIndex(child => child.id === node.id);
      if (childIndex !== -1) {
        // Check if the child has already been positioned during reorganization
        if (alreadyPositionedNodes && alreadyPositionedNodes.has(node.id)) {
          console.log(`[DEBUG] repositionChildrenCentered: Child ${node.id} already positioned by another parent, skipping repositioning`);
          return node; // Child has already been positioned, don't move it
        }
        
        // Mark this child as positioned
        if (alreadyPositionedNodes) {
          alreadyPositionedNodes.add(node.id);
        }
        
        const newX = startX + childIndex * childSpacing;
        const newY = parentNode.position.y + parentDimensions.verticalSpacing;
        console.log(`[DEBUG] repositionChildrenCentered: Moving child ${node.id} from (${node.position.x}, ${node.position.y}) to (${newX}, ${newY}) using dynamic spacing`);
        return {
          ...node,
          position: {
            x: newX,
            y: newY
          }
        };
      }
      return node;
    });
  }, [getNodeChildren, getNodeDimensions]);

  // Position decision node children when they are linked via dropdown
  const positionDecisionNodeChildren = useCallback((decisionNodeId: string, decisionData: any) => {
    console.log(`[DEBUG] Positioning decision node children for node ${decisionNodeId}`, decisionData);
    
    if (!decisionData) return;
    
    setNodes(currentNodes => {
      const decisionNode = currentNodes.find(n => n.id === decisionNodeId);
      if (!decisionNode) {
        console.log(`[DEBUG] Decision node ${decisionNodeId} not found`);
        return currentNodes;
      }
      
      return currentNodes.map(node => {
        // Skip the decision node itself
        if (node.id === decisionNodeId) return node;
        
        // Check if this node is linked to the decision node
        const nodeIdentifier = node.data.identifier || node.id;
        const isTruePath = decisionData.truePath === nodeIdentifier;
        const isFalsePath = decisionData.falsePath === nodeIdentifier;
        
        if (!isTruePath && !isFalsePath) return node;
        
        // Use the smart positioning logic to calculate new position
        const newPosition = calculateSmartPosition(decisionNode, node, currentNodes, edges);
        
        console.log(`[DEBUG] Repositioning linked ${isTruePath ? 'true' : 'false'} path node ${node.id} to ${JSON.stringify(newPosition)}`);
        
        // Mark as manually moved to preserve this positioning
        setManuallyMovedNodes(prev => {
          const newSet = new Set(prev);
          newSet.add(node.id);
          return newSet;
        });
        
        return {
          ...node,
          position: newPosition
        };
      });
    });
  }, [calculateSmartPosition, edges, setNodes, setManuallyMovedNodes]);

  // Helper function to process response variables from tool calls and add them to global registry
  const processResponseVariables = useCallback((toolCalls: ToolCall[]) => {
    const newPlaceholders: Record<string, any> = {};
    
    toolCalls.forEach(toolCall => {
      if (toolCall.responseVariables) {
        toolCall.responseVariables.forEach(responseVar => {
          if (responseVar.name && responseVar.values && responseVar.values.length > 0) {
            // Check if this response variable already exists in the global registry
            if (!globalVariableRegistry[responseVar.name]) {
              newPlaceholders[responseVar.name] = {
                name: responseVar.name,
                values: responseVar.values,
                persist: false // Default persist to false
              };
            } else {
              // If it exists, merge the values (avoid duplicates)
              const existingValues = globalVariableRegistry[responseVar.name].values || [];
              const newValues = responseVar.values.filter(value => !existingValues.includes(value));
              if (newValues.length > 0) {
                newPlaceholders[responseVar.name] = {
                  ...globalVariableRegistry[responseVar.name],
                  values: [...existingValues, ...newValues]
                };
              }
            }
          }
        });
      }
    });
    
    // Update global registry if we have new placeholders
    if (Object.keys(newPlaceholders).length > 0) {
      setGlobalVariableRegistry(prev => ({
        ...prev,
        ...newPlaceholders
      }));
    }
  }, [globalVariableRegistry, setGlobalVariableRegistry]);

  const handleOk = useCallback(() => {
    
    // Check if we have valid content OR valid tool calls OR valid decision data
    const hasValidToolCalls = modalState.newNodeEntityType === "BOT" && modalState.hasToolCall && modalState.toolCalls.some((tc: any) => tc.name && tc.name.trim());
    const hasValidContent = modalState.newNodeContent && modalState.newNodeContent.trim();
    const hasValidDecisionData = modalState.newNodeEntityType === "DECISION" && 
      modalState.decisionData.conditions.length > 0 &&
      modalState.decisionData.conditions.every((cond: any) => 
        cond.variable && cond.operator && (cond.type === "boolean" || (cond.type === "value" && cond.value))
      ) &&
      (modalState.decisionData.truePath || modalState.decisionData.falsePath);
    
    if ((!modalState.sourceNodeId && !modalState.isEditMode) || (!hasValidContent && !hasValidToolCalls && !hasValidDecisionData)) {
      return;
    }

    // If not editing and this is a new node, check for duplicate identifiers
    if (!modalState.isEditMode && modalState.sourceNodeId && modalState.newNodeIdentifier) {
      if (hasChildWithIdentifier(modalState.sourceNodeId, modalState.newNodeIdentifier)) {
        message.error(`This parent already has a child with the "${modalState.newNodeIdentifier}" identifier.`);
        return;
      }
    }

    // If editing, check if we're trying to change to an identifier that would create a duplicate
    if (modalState.isEditMode && modalState.nodeToEdit && modalState.newNodeIdentifier) {
      // Get parent ID
      const parentEdge = edges.find(edge => edge.target === modalState.nodeToEdit);
      if (parentEdge) {
        const parentId = parentEdge.source;
        // Check for siblings with same identifier, excluding this node
        const childrenIds = getNodeChildren(parentId, edges);
        const hasDuplicateIdentifier = childrenIds.some(childId => {
          const childNode = nodes.find(node => node.id === childId);
          return childNode && 
                 childNode.id !== modalState.nodeToEdit && // Exclude self
                 childNode.data.identifier === modalState.newNodeIdentifier;
        });
        
        if (hasDuplicateIdentifier) {
          message.error(`This parent already has another child with the "${modalState.newNodeIdentifier}" identifier.`);
          return;
        }
      }
    }

    // Check if we're trying to connect to an existing node (for decision nodes)
    if (!modalState.isEditMode && modalState.sourceNodeId && modalState.newNodeIdentifier) {
      const sourceNode = nodes.find(n => n.id === modalState.sourceNodeId);
      const isConnectingToDecisionNode = sourceNode?.data.entityType === "DECISION";
      
      if (isConnectingToDecisionNode) {
        // Check if a node with this identifier already exists in the tree
        const existingNode = nodes.find(n => n.data.identifier === modalState.newNodeIdentifier);
        
        if (existingNode) {
          // Node exists - check if it's a free child or has children
          const existingNodeHasChildren = nodeHasChildren(existingNode.id);
          
          // If the existing node has no children, we can reposition it properly
          if (!existingNodeHasChildren) {
            // Calculate proper position for this existing node
            const decisionData = sourceNode.data.decisionData || { truePath: null, falsePath: null, conditions: [] };
            const hasTruePath = !!decisionData.truePath;
            const hasFalsePath = !!decisionData.falsePath;
            
            // Determine if this will be true path or false path
            const willBeTruePath = !hasTruePath && !hasFalsePath ? true : 
                                  !hasTruePath && hasFalsePath ? true : 
                                  hasTruePath && !hasFalsePath ? false : false;
            
            const NODE_WIDTH = 280;
            const HORIZONTAL_SPACING = 150;
            const VERTICAL_SPACING = 180;
            
            // Position true path to the left, false path to the right
            const xOffset = willBeTruePath ? -HORIZONTAL_SPACING : HORIZONTAL_SPACING;
            
            const newPosition = {
              x: sourceNode.position.x + xOffset,
              y: sourceNode.position.y + VERTICAL_SPACING
            };
            
            // Update the existing node's position
            setNodes(nds => nds.map(node => 
              node.id === existingNode.id 
                ? { ...node, position: newPosition }
                : node
            ));
            
            // Mark the repositioned node as manually moved
            setManuallyMovedNodes(prev => {
              const newSet = new Set(prev);
              newSet.add(existingNode.id);
              return newSet;
            });
          }
          
          // Link it to the decision node
          const decisionData = sourceNode.data.decisionData || { truePath: null, falsePath: null, conditions: [] };
          const hasTruePath = !!decisionData.truePath;
          const hasFalsePath = !!decisionData.falsePath;
          
          const updatedDecisionData = { ...decisionData };
          
          if (!hasTruePath && !hasFalsePath) {
            // No paths exist, set as true path
            updatedDecisionData.truePath = modalState.newNodeIdentifier;
          } else if (hasTruePath && !hasFalsePath) {
            // Only true path exists, set as false path
            updatedDecisionData.falsePath = modalState.newNodeIdentifier;
          } else if (!hasTruePath && hasFalsePath) {
            // Only false path exists, set as true path
            updatedDecisionData.truePath = modalState.newNodeIdentifier;
          } else {
            // Both paths exist - show error
            message.error("Decision node already has both true and false paths assigned.");
            return;
          }
          
          // Update the decision node with the new path
          setNodes(nds => nds.map(node => 
            node.id === modalState.sourceNodeId 
              ? { ...node, data: { ...node.data, decisionData: updatedDecisionData } }
              : node
          ));
          
          // Create decision edge with the appropriate label
          const isTrue = updatedDecisionData.truePath === modalState.newNodeIdentifier;
          const edgeLabel = isTrue ? "True" : "False";
          const labelStyle = isTrue 
            ? { fill: "#52c41a", fontWeight: 600 }
            : { fill: "#f5222d", fontWeight: 600 };
          const labelBgStyle = isTrue 
            ? { fill: "#f6ffed" }
            : { fill: "#fff2f0" };
          
          const decisionEdgeId = `e${modalState.sourceNodeId}-${existingNode.id}_${isTrue ? "true" : "false"}`;
          
          setEdges(eds => {
            // Check if edge already exists
            const existingEdge = eds.find(edge => edge.id === decisionEdgeId);
            if (existingEdge) {
              return eds; // Don't add duplicate
            }
            const isUp = isUpstreamConnection(modalState.sourceNodeId, existingNode.id, nodes, eds);
            const edgeStyle = isUp
              ? { ...UPSTREAM_EDGE_STYLE }
              : (isTrue ? { stroke: "#52c41a", strokeWidth: 2 } : { stroke: "#f5222d", strokeWidth: 2 });
            
            const decisionEdge: Edge = {
              id: decisionEdgeId,
              source: modalState.sourceNodeId,
              target: existingNode.id,
              type: "custom",
              label: edgeLabel,
              style: edgeStyle,
              labelStyle,
              labelBgStyle
            };
            
            return [...eds, decisionEdge];
          });
          
          // Reset form state and exit early
          resetModalState();
          const repositionMessage = existingNodeHasChildren 
            ? `Successfully linked existing node "${modalState.newNodeIdentifier}" to decision path.`
            : `Successfully linked and repositioned existing node "${modalState.newNodeIdentifier}" to decision path.`;
          message.success(repositionMessage);
          return;
        }
      }
    }

    // Extract placeholders from content
    const detectedPlaceholders = extractPlaceholders(modalState.newNodeContent);
    const nodePlaceholders: Record<string, VariablePlaceholder> = {};
    
    // Initialize placeholders with current examples or from global registry
    detectedPlaceholders.forEach(name => {
      // Check if this placeholder exists in the global registry first
      if (globalVariableRegistry[name]) {
        // Use the global registry version (empty local placeholder to indicate it uses global)
        nodePlaceholders[name] = { name, values: [], persist: false };
      } else if (placeholders[name]) {
        // Use the local placeholder
        nodePlaceholders[name] = { 
          name, 
          values: placeholders[name]?.values || [],
          persist: placeholders[name]?.persist || false
        };
        
        // Add to global registry if not already there
        setGlobalVariableRegistry(prev => ({
          ...prev,
          [name]: placeholders[name]
        }));
      } else {
        // Create a new placeholder
        nodePlaceholders[name] = { name, values: [], persist: false };
        
        // Add to global registry
        setGlobalVariableRegistry(prev => ({
          ...prev,
          [name]: { name, values: [], persist: false }
        }));
      }
    });

    // Save current state to history if adding a new node
    if (!modalState.isEditMode) {
      saveToHistory("add");
    }

    // Flag to track if we need to reorganize the tree
    // Don't reorganize for child nodes since we position them intelligently
    const isAddingChildNode = !modalState.isEditMode && modalState.sourceNodeId;
    const sourceNode = nodes.find(n => n.id === modalState.sourceNodeId);
    // Debug: Check what nodes we have
    // console.log(`[DEBUG] All nodes:`, nodes.map(n => ({ id: n.id, entityType: n.data.entityType, label: n.data.label })));
    // Check if this is actually a decision node (could be mistyped as BOT but have decision data)
    const isActuallyDecisionNode = sourceNode?.data.entityType === "DECISION" || 
                                   (sourceNode?.data.decisionData && Object.keys(sourceNode.data.decisionData).length > 0);
    const isAddingToDecisionNode = isActuallyDecisionNode;
    
    const shouldReorganize = !modalState.isEditMode && !isAddingChildNode && !isAddingToDecisionNode;

    if (modalState.isEditMode && modalState.nodeToEdit) {
      // Update the existing node
      // FIXED: Use the modal state tool calls (which contain the user's updates from NodeModal)
    // The modal state has the NEW truth that the user just entered
    
    // Use modal state tool calls (user's latest input) for BOT nodes with tool calls enabled
    const toolCallsToUse = modalState.newNodeEntityType === "BOT" && modalState.hasToolCall
      ? (modalState.toolCalls || []) // Use the updated tool calls from modal state
      : [];
    
    // Process response variables from tool calls and add them to global registry
    if (toolCallsToUse.length > 0) {
      processResponseVariables(toolCallsToUse);
    }
      
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === modalState.nodeToEdit) {
            const updatedNode = {
              ...node,
              data: {
                ...node.data,
                content: modalState.newNodeContent,
                prefix: modalState.newNodePrefix,
                suffix: modalState.newNodeSuffix,
                decisionData: modalState.newNodeEntityType === "DECISION" ? modalState.decisionData : undefined,
                entityType: modalState.newNodeEntityType,
                identifier: modalState.newNodeIdentifier,
                isDestinationNode: modalState.isDestinationNode, // Update destination node status
                potentialStart: modalState.potentialStart, // Update potential start status
                placeholders: nodePlaceholders,
                toolCalls: toolCallsToUse, // Use modal state tool calls (user's latest updates from NodeModal)
                variableAssignments: modalState.variableAssignments, // Add variable assignments
              },
            };
            return updatedNode;
          }
          return node;
        })
      );
      
      // Handle nextNodeId/nextNodeIds edge management for non-DECISION nodes
      if (modalState.newNodeEntityType !== "DECISION" && modalState.nodeToEdit) {
        setTimeout(() => {
          setEdges(currentEdges => {
            // Remove existing outgoing edges from this node (but keep all edges from other nodes)
            const filteredEdges = currentEdges.filter(edge => 
              edge.source !== modalState.nodeToEdit
            );
            // Determine all targets from nextNodeIds (multi) or fallback to nextNodeId
            const targets = (modalState.nextNodeIds && modalState.nextNodeIds.length > 0)
              ? modalState.nextNodeIds
              : (modalState.nextNodeId && modalState.nextNodeId !== "" ? [modalState.nextNodeId] : []);

            let updatedEdges = [...filteredEdges];
            if (targets.length > 0 && !modalState.isDestinationNode) {
              for (const targetId of targets) {
                const newEdgeId = `e${modalState.nodeToEdit}-${targetId}`;
                const edgeExists = updatedEdges.some(edge => edge.id === newEdgeId);
                if (!edgeExists) {
                  const isUp = isUpstreamConnection(modalState.nodeToEdit!, String(targetId), nodes, updatedEdges);
                  updatedEdges.push({
                    id: newEdgeId,
                    source: modalState.nodeToEdit,
                    target: targetId,
                    type: "custom",
                    ...(isUp ? { style: { ...UPSTREAM_EDGE_STYLE } } : {})
                  });
                }
              }
              // Reposition only standalone newly linked children for non-decision nodes
              const parentNode = nodes.find(n => n.id === modalState.nodeToEdit);
              if (parentNode && parentNode.data.entityType !== "DECISION") {
                console.log(`[DEBUG] Centering: Next node field edges added. Will reposition only standalone newly linked children for ${parentNode.data.entityType} parent ${modalState.nodeToEdit}`);

                // Identify which of the newly linked targets are standalone (no incoming edges before linking)
                const movableTargets = new Set<string>();
                const parentId = modalState.nodeToEdit!;

                for (const rawTarget of targets) {
                  const targetId = String(rawTarget);
                  const hadIncomingBefore = filteredEdges.some(e => e.target === targetId);

                  // Detect if target is upstream of parent (link would create a cycle); if so, do not move it
                  let isUpstream = false;
                  try {
                    const descendantsOfTarget = getNodeDescendants
                      ? getNodeDescendants(targetId, nodes, updatedEdges)
                      : [];
                    isUpstream = descendantsOfTarget.includes(parentId);
                  } catch (_e) {
                    // If helper is unavailable, default to conservative: don't treat as movable
                    isUpstream = true;
                  }

                  if (!hadIncomingBefore && !isUpstream) {
                    movableTargets.add(targetId);
                  }
                }

                // Build protected set = all current children except the standalone new targets
                const childIdsAfter = updatedEdges
                  .filter(e => e.source === parentId)
                  .map(e => e.target);
                const protectedSet = new Set<string>(
                  childIdsAfter.filter(id => !movableTargets.has(String(id)))
                );

                setTimeout(() => {
                  setNodes(currentNodes => repositionChildrenCentered(parentId, currentNodes, updatedEdges, protectedSet));
                }, 50);
              }
            }

            return updatedEdges;
          });
        }, 10);
      }
      
      // Create decision edges if this is a decision node
      if (modalState.newNodeEntityType === "DECISION" && modalState.decisionData && modalState.nodeToEdit) {
        setTimeout(() => {
          setEdges(currentEdges => {
            const nonDecisionEdges = currentEdges.filter(edge => 
              edge.source !== modalState.nodeToEdit || 
              (!edge.id.includes("_true") && !edge.id.includes("_false"))
            );
            
            const newDecisionEdges: Edge[] = [];
            
            if (modalState.decisionData.truePath) {
              const truePathNodeId = resolveIdentifierToNodeId(modalState.decisionData.truePath, nodes);
              if (truePathNodeId) {
                const isUp = isUpstreamConnection(modalState.nodeToEdit!, truePathNodeId, nodes, currentEdges);
                newDecisionEdges.push({
                  id: `e${modalState.nodeToEdit}-${truePathNodeId}_true`,
                  source: modalState.nodeToEdit,
                  target: truePathNodeId,
                  type: "custom",
                  label: "True",
                  style: isUp ? { ...UPSTREAM_EDGE_STYLE } : { stroke: "#52c41a", strokeWidth: 2 },
                  labelStyle: { fill: "#52c41a", fontWeight: 600 },
                  labelBgStyle: { fill: "#f6ffed" }
                });
              }
            }
            
            if (modalState.decisionData.falsePath) {
              const falsePathNodeId = resolveIdentifierToNodeId(modalState.decisionData.falsePath, nodes);
              if (falsePathNodeId) {
                const isUp = isUpstreamConnection(modalState.nodeToEdit!, falsePathNodeId, nodes, currentEdges);
                newDecisionEdges.push({
                  id: `e${modalState.nodeToEdit}-${falsePathNodeId}_false`,
                  source: modalState.nodeToEdit,
                  target: falsePathNodeId,
                  type: "custom",
                  label: "False",
                  style: isUp ? { ...UPSTREAM_EDGE_STYLE } : { stroke: "#f5222d", strokeWidth: 2 },
                  labelStyle: { fill: "#f5222d", fontWeight: 600 },
                  labelBgStyle: { fill: "#fff2f0" }
                });
              }
            }
            
            return deduplicateEdges([...nonDecisionEdges, ...newDecisionEdges]);
          });
        }, 50);
        
        // Position the linked decision node children after edges are created
        setTimeout(() => {
          positionDecisionNodeChildren(modalState.nodeToEdit!, modalState.decisionData);
        }, 100);
      }
      
      // Update node handlers immediately after editing to ensure the UI reflects the new data
      setTimeout(() => {
        updateNodeHandlers();
      }, 0);
      

    } else if (isFirstNode) {
      // Replace the placeholder node with the first real node
      debugLog(`Replacing placeholder node with first real node. isFirstNode: ${isFirstNode}, sourceNodeId: ${modalState.sourceNodeId}`);
      
      // Ensure the first real node gets ID 1
      resetNodeIdCounter();
      
      // Calculate the sequential ID outside of the setNodes callback to prevent multiple calls
      const sequentialId = getNextNodeId().toString();
      
      // Prepare tool call data if this is a BOT node and has tool call enabled
      let toolCallsData: ToolCall[] = [];
      if (modalState.newNodeEntityType === "BOT" && modalState.hasToolCall) {
        toolCallsData = modalState.toolCalls.filter((tc: any) => tc.name && tc.name.trim());
        
        // Process response variables from tool calls and add them to global registry
        if (toolCallsData.length > 0) {
          processResponseVariables(toolCallsData);
        }
      }
      
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === modalState.sourceNodeId) {
            return {
              ...node,
              id: sequentialId, // Use sequential ID as actual node ID
              data: {
                ...node.data,
                label: modalState.newNodeEntityType,
                content: modalState.newNodeContent,
                prefix: modalState.newNodePrefix,
                suffix: modalState.newNodeSuffix,
                decisionData: modalState.newNodeEntityType === "DECISION" ? modalState.decisionData : undefined,
                isPlaceholder: false,
                hasChildren: false,
                entityType: modalState.newNodeEntityType,
                identifier: modalState.newNodeIdentifier,
                isDestinationNode: modalState.isDestinationNode, // Set destination node status
                potentialStart: modalState.potentialStart, // Set potential start status
                placeholders: nodePlaceholders,
                globalRegistry: globalVariableRegistry,
                toolCalls: toolCallsData, // Add tool calls data
                variableAssignments: modalState.variableAssignments, // Add variable assignments
                onPlaceholderClick: () => {},
                onAddNode: () => {},
                onEditPlaceholder: () => {},
                onEditNode: () => {},
                onDeleteNode: () => {},
              },
            };
          }
          return node;
        })
      );
      
      // Also update any edges that reference the old placeholder ID
      setEdges((eds) =>
        eds.map((edge) => ({
          ...edge,
          source: edge.source === modalState.sourceNodeId ? sequentialId : edge.source,
          target: edge.target === modalState.sourceNodeId ? sequentialId : edge.target,
        }))
      );
      
      setIsFirstNode(false);
      
      // Create decision edges if this is a decision node (first node scenario)
      if (modalState.newNodeEntityType === "DECISION" && modalState.decisionData) {
        setTimeout(() => {
          setEdges(currentEdges => {
            const nonDecisionEdges = currentEdges.filter(edge => 
              edge.source !== sequentialId || 
              (!edge.id.includes("_true") && !edge.id.includes("_false"))
            );
            
            const newDecisionEdges: Edge[] = [];
            
            if (modalState.decisionData.truePath) {
              const truePathNodeId = resolveIdentifierToNodeId(modalState.decisionData.truePath, nodes);
              if (truePathNodeId) {
                const isUp = isUpstreamConnection(sequentialId, truePathNodeId, nodes, currentEdges);
                newDecisionEdges.push({
                  id: `e${sequentialId}-${truePathNodeId}_true`,
                  source: sequentialId,
                  target: truePathNodeId,
                  type: "custom",
                  label: "True",
                  style: isUp ? { ...UPSTREAM_EDGE_STYLE } : { stroke: "#52c41a", strokeWidth: 2 },
                  labelStyle: { fill: "#52c41a", fontWeight: 600 },
                  labelBgStyle: { fill: "#f6ffed" }
                });
              }
            }
            
            if (modalState.decisionData.falsePath) {
              const falsePathNodeId = resolveIdentifierToNodeId(modalState.decisionData.falsePath, nodes);
              if (falsePathNodeId) {
                const isUp = isUpstreamConnection(sequentialId, falsePathNodeId, nodes, currentEdges);
                newDecisionEdges.push({
                  id: `e${sequentialId}-${falsePathNodeId}_false`,
                  source: sequentialId,
                  target: falsePathNodeId,
                  type: "custom",
                  label: "False",
                  style: isUp ? { ...UPSTREAM_EDGE_STYLE } : { stroke: "#f5222d", strokeWidth: 2 },
                  labelStyle: { fill: "#f5222d", fontWeight: 600 },
                  labelBgStyle: { fill: "#fff2f0" }
                });
              }
            }
            
            return deduplicateEdges([...nonDecisionEdges, ...newDecisionEdges]);
          });
        }, 100);
      }
      
      // Trigger repositioning after state update
      if (shouldReorganize) {
        setShouldRepositionAfterNodeAddition(true);
      }
    } else {
      // Adding a child node to an existing node
      debugLog(`Adding child node to existing node. isFirstNode: ${isFirstNode}, sourceNodeId: ${modalState.sourceNodeId}`);
      const parentNode = nodes.find((node) => node.id === modalState.sourceNodeId);
      
      // Calculate a smart position for the new node relative to the parent
      let newPosition = { x: 250, y: 100 };
      
      if (parentNode) {
        const NODE_WIDTH = 280;
        const NODE_HEIGHT = 120;
        const HORIZONTAL_SPACING = 150;
        const VERTICAL_SPACING = 180;
        
        // Special positioning logic for decision nodes
        if (parentNode.data.entityType === "DECISION") {
          // SIMPLIFIED APPROACH: Always position first child to left, second to right
          // Count existing decision paths
          const decisionData = parentNode.data.decisionData || { truePath: null, falsePath: null, conditions: [] };
          const existingPaths = [decisionData.truePath, decisionData.falsePath].filter(Boolean).length;
          
          // Simple positioning: first child goes left (-150px), second child goes right (+150px)
          let xOffset = -HORIZONTAL_SPACING; // Default to left (true path)
          if (existingPaths === 1) {
            // If there's already one path, this will be the second path (right side)
            xOffset = HORIZONTAL_SPACING;
          }
          
          newPosition = {
            x: parentNode.position.x + xOffset,
            y: parentNode.position.y + VERTICAL_SPACING
          };
          
          // Debug positioning if needed
          // console.log(`Decision node positioning:`, { parentId: parentNode.id, xOffset, newPosition });
        } else {
          // Regular node positioning logic
          const childNodeIds = getNodeChildren(parentNode.id, edges);
          const existingChildren = childNodeIds.map(id => nodes.find(n => n.id === id)).filter(Boolean);
          
          if (existingChildren.length === 0) {
            // First child - position directly below parent
            newPosition = {
              x: parentNode.position.x,
              y: parentNode.position.y + VERTICAL_SPACING
            };
          } else {
            // Multiple children - position to the right of the rightmost child
            const rightmostChild = existingChildren.reduce((rightmost, child) => {
              return child!.position.x > rightmost!.position.x ? child : rightmost;
            });
            
            newPosition = {
              x: rightmostChild!.position.x + NODE_WIDTH + (HORIZONTAL_SPACING / 2),
              y: rightmostChild!.position.y
            };
          }
        }
      }
      
      // Add identifier to the list if it's a new one
      if (modalState.newNodeIdentifier && 
          !identifiers.includes(modalState.newNodeIdentifier)) {
        setIdentifiers(prev => [...prev, modalState.newNodeIdentifier]);
      }
      
      // Calculate the sequential ID outside of the node creation to prevent multiple calls
      const sequentialId = getNextNodeId().toString();
      
      // Store the decision node ID for later repositioning if this is a DECISION node
      let newDecisionNodeId: string | null = null;
      if (modalState.newNodeEntityType === "DECISION") {
        newDecisionNodeId = sequentialId;
        console.log(`[DEBUG] New decision node will have ID: ${newDecisionNodeId}`);
      }
      
      // Prepare tool call data if this is a BOT node and has tool call enabled
      let toolCallsData: ToolCall[] = [];
      if (modalState.newNodeEntityType === "BOT" && modalState.hasToolCall) {
        toolCallsData = modalState.toolCalls.filter((tc: any) => tc.name && tc.name.trim());
        
        // Process response variables from tool calls and add them to global registry
        if (toolCallsData.length > 0) {
          processResponseVariables(toolCallsData);
        }
      }
      
      // Create new node with opposite entity type
      const newNode: Node = {
        id: sequentialId,
        type: "custom",
        position: newPosition,
        data: {
          label: modalState.newNodeEntityType,
          content: modalState.newNodeContent,
          prefix: modalState.newNodePrefix,
          suffix: modalState.newNodeSuffix,
          decisionData: modalState.newNodeEntityType === "DECISION" ? modalState.decisionData : undefined,
          isPlaceholder: false,
          hasChildren: false,
          entityType: modalState.newNodeEntityType,
          identifier: modalState.newNodeIdentifier,
          isDestinationNode: modalState.isDestinationNode, // Set destination node status
          potentialStart: modalState.potentialStart, // Set potential start status
          isDescendantOfDestination: false,
          placeholders: nodePlaceholders,
          globalRegistry: globalVariableRegistry,
          toolCalls: toolCallsData, // Add tool calls data
          variableAssignments: modalState.variableAssignments, // Add variable assignments
          onPlaceholderClick: () => {},
          onAddNode: () => {},
          onEditPlaceholder: () => {},
          onEditNode: () => {},
          onDeleteNode: () => {},
        },
      };

      // For decision node children, mark as manually moved BEFORE adding to prevent any repositioning
      const isAddingToDecisionNode = parentNode?.data.entityType === "DECISION";
      if (isAddingToDecisionNode) {
        setManuallyMovedNodes(prev => {
          const newSet = new Set(prev);
          newSet.add(newNode.id);
          return newSet;
        });
      }
      
              // Add the node
      setNodes(nds => [...nds, newNode]);
      
      // If this is a new DECISION node, reposition any temporary children
      if (newDecisionNodeId) {
        // Use a longer delay to ensure the decision node has been added to React state
        setTimeout(() => {
          console.log(`[DEBUG] Repositioning temporary children for new decision node ID: ${newDecisionNodeId}`);
          repositionTemporaryDecisionChildren(newDecisionNodeId);
        }, 300);
      }
      
      // Mark as manually positioned for all child nodes to prevent future auto-repositioning
      if (!isAddingToDecisionNode) {
        setManuallyMovedNodes(prev => {
          const newSet = new Set(prev);
          newSet.add(newNode.id);
          return newSet;
        });
      }
      
      // Handle next node assignment for new nodes (creates parent-child relationship)
      if (modalState.nextNodeId && modalState.nextNodeId !== "" && modalState.newNodeEntityType !== "DECISION") {
        setTimeout(() => {
          setEdges(currentEdges => {
            const nextEdgeId = `e${newNode.id}-${modalState.nextNodeId}`;
            // Check if edge already exists
            const existingEdge = currentEdges.find(edge => edge.id === nextEdgeId);
            if (existingEdge) {
              return currentEdges; // Don't add duplicate
            }
            
            const isUp = isUpstreamConnection(newNode.id, modalState.nextNodeId, nodes, currentEdges);
            const nextEdge: Edge = {
              id: nextEdgeId,
              source: newNode.id,
              target: modalState.nextNodeId,
              type: "custom",
              ...(isUp ? { style: { ...UPSTREAM_EDGE_STYLE } } : {})
            };
            
            const updatedEdges = [...currentEdges, nextEdge];
            
            // After edge is added, reposition children for the new node (if it becomes a parent)
            console.log(`[DEBUG] Centering: New node next field edge added, will reposition children for ${modalState.newNodeEntityType} node ${newNode.id}`);
            setTimeout(() => {
              setNodes(currentNodes => {
                console.log(`[DEBUG] Centering: Running repositionChildrenCentered for new node ${newNode.id} after next node assignment`);
                return repositionChildrenCentered(newNode.id, currentNodes, updatedEdges);
              });
            }, 50);
            
            return updatedEdges;
          });
        }, 50);
      }
      
      if (isAddingToDecisionNode) {
        // Auto-assign the new node to the appropriate decision path
        const decisionData = sourceNode.data.decisionData || { truePath: null, falsePath: null, conditions: [] };
        const hasTruePath = !!decisionData.truePath;
        const hasFalsePath = !!decisionData.falsePath;
        
        const updatedDecisionData = { ...decisionData };
        
        // Use identifier if available, otherwise use node ID (for backward compatibility)
        const nodeReference = newNode.data.identifier || newNode.id;
        
        if (!hasTruePath && !hasFalsePath) {
          // No paths exist, set as true path
          updatedDecisionData.truePath = nodeReference;
        } else if (hasTruePath && !hasFalsePath) {
          // Only true path exists, set as false path
          updatedDecisionData.falsePath = nodeReference;
        } else if (!hasTruePath && hasFalsePath) {
          // Only false path exists, set as true path
          updatedDecisionData.truePath = nodeReference;
        }
        
        // Update the decision node with the new path
        setNodes(nds => nds.map(node => 
          node.id === modalState.sourceNodeId 
            ? { ...node, data: { ...node.data, decisionData: updatedDecisionData } }
            : node
        ));
        
        // Create decision edge with the appropriate label
        const isTrue = updatedDecisionData.truePath === nodeReference;
        const edgeLabel = isTrue ? "True" : "False";
        const edgeStyle = isTrue 
          ? { stroke: "#52c41a", strokeWidth: 2 }
          : { stroke: "#f5222d", strokeWidth: 2 };
        const labelStyle = isTrue 
          ? { fill: "#52c41a", fontWeight: 600 }
          : { fill: "#f5222d", fontWeight: 600 };
        const labelBgStyle = isTrue 
          ? { fill: "#f6ffed" }
          : { fill: "#fff2f0" };
        
        const decisionEdgeId = `e${modalState.sourceNodeId}-${newNode.id}_${isTrue ? "true" : "false"}`;
        
        setEdges(eds => {
          // Check if edge already exists
          const existingEdge = eds.find(edge => edge.id === decisionEdgeId);
          if (existingEdge) {
            return eds; // Don't add duplicate
          }
          
          const decisionEdge: Edge = {
            id: decisionEdgeId,
            source: modalState.sourceNodeId,
            target: newNode.id,
            type: "custom",
            label: edgeLabel,
            style: edgeStyle,
            labelStyle: labelStyle,
            labelBgStyle: labelBgStyle
          };
          return [...eds, decisionEdge];
        });
      } else {
        // Regular node addition logic
        // Only create regular edge if this is NOT a decision node
        // Decision nodes will create their own special edges to target nodes
        if (modalState.newNodeEntityType !== "DECISION") {
          const edgeId = `e${modalState.sourceNodeId}-${newNode.id}`;
          setEdges(eds => {
            // Check if edge already exists
            const existingEdge = eds.find(edge => edge.id === edgeId);
            if (existingEdge) {
              return eds; // Don't add duplicate
            }
            
            const isUp = isUpstreamConnection(modalState.sourceNodeId!, newNode.id, nodes, eds);
            const newEdge: Edge = {
              id: edgeId,
              source: modalState.sourceNodeId,
              target: newNode.id,
              type: "custom",
              ...(isUp ? { style: { ...UPSTREAM_EDGE_STYLE } } : {})
            };
            
            const updatedEdges = [...eds, newEdge];
            
            // After edge is added, reposition children for any parent node (except DECISION nodes)
            const parentNode = nodes.find(n => n.id === modalState.sourceNodeId);
            if (parentNode && parentNode.data.entityType !== "DECISION") {
              console.log(`[DEBUG] Centering: Regular edge added, will reposition children for ${parentNode.data.entityType} parent ${modalState.sourceNodeId}`);
              setTimeout(() => {
                setNodes(currentNodes => {
                  console.log(`[DEBUG] Centering: Running repositionChildrenCentered for parent ${modalState.sourceNodeId} after regular edge creation`);
                  return repositionChildrenCentered(modalState.sourceNodeId!, currentNodes, updatedEdges);
                });
              }, 50);
            }
            
            return updatedEdges;
          });
        } else {
          // For decision nodes, create edge from parent to decision node
          const edgeId = `e${modalState.sourceNodeId}-${newNode.id}`;
          setEdges(eds => {
            // Check if edge already exists
            const existingEdge = eds.find(edge => edge.id === edgeId);
            if (existingEdge) {
              return eds; // Don't add duplicate
            }
            
            const isUp = isUpstreamConnection(modalState.sourceNodeId!, newNode.id, nodes, eds);
            const parentEdge: Edge = {
              id: edgeId,
              source: modalState.sourceNodeId,
              target: newNode.id,
              type: "custom",
              ...(isUp ? { style: { ...UPSTREAM_EDGE_STYLE } } : {})
            };
            return [...eds, parentEdge];
          });
        }
      }
      
      // Create decision edges if this is a decision node AND we're not adding to another decision node
      // (When adding to a decision node, we only want the True/False edge, not outgoing edges from the new node)
      if (modalState.newNodeEntityType === "DECISION" && modalState.decisionData && !isAddingToDecisionNode) {
        setTimeout(() => {
          setEdges(currentEdges => {
            const nonDecisionEdges = currentEdges.filter(edge => 
              edge.source !== sequentialId || 
              (!edge.id.includes("_true") && !edge.id.includes("_false"))
            );
            
            const newDecisionEdges: Edge[] = [];
            
            if (modalState.decisionData.truePath) {
              const truePathNodeId = resolveIdentifierToNodeId(modalState.decisionData.truePath, nodes);
              if (truePathNodeId) {
                newDecisionEdges.push({
                  id: `e${sequentialId}-${truePathNodeId}_true`,
                  source: sequentialId,
                  target: truePathNodeId,
                  type: "custom",
                  label: "True",
                  style: { stroke: "#52c41a", strokeWidth: 2 },
                  labelStyle: { fill: "#52c41a", fontWeight: 600 },
                  labelBgStyle: { fill: "#f6ffed" }
                });
              }
            }
            
            if (modalState.decisionData.falsePath) {
              const falsePathNodeId = resolveIdentifierToNodeId(modalState.decisionData.falsePath, nodes);
              if (falsePathNodeId) {
                newDecisionEdges.push({
                  id: `e${sequentialId}-${falsePathNodeId}_false`,
                  source: sequentialId,
                  target: falsePathNodeId,
                  type: "custom",
                  label: "False",
                  style: { stroke: "#f5222d", strokeWidth: 2 },
                  labelStyle: { fill: "#f5222d", fontWeight: 600 },
                  labelBgStyle: { fill: "#fff2f0" }
                });
              }
            }
            
            return deduplicateEdges([...nonDecisionEdges, ...newDecisionEdges]);
          });
        }, 150);
      }
      
      // Handle nextNodeId edge creation for non-DECISION new nodes
      if (modalState.newNodeEntityType !== "DECISION" && modalState.nextNodeId && modalState.nextNodeId !== "") {
        setTimeout(() => {
          setEdges(eds => {
            const nextEdgeId = `e${newNode.id}-${modalState.nextNodeId}`;
            // Check if edge already exists
            const existingEdge = eds.find(edge => edge.id === nextEdgeId);
            if (existingEdge) {
              return eds; // Don't add duplicate
            }
            
            const isUp = isUpstreamConnection(newNode.id, modalState.nextNodeId, nodes, eds);
            const nextEdge: Edge = {
              id: nextEdgeId,
              source: newNode.id,
              target: modalState.nextNodeId,
              type: "custom",
              ...(isUp ? { style: { ...UPSTREAM_EDGE_STYLE } } : {})
            };
            return [...eds, nextEdge];
          });
        }, 200); // Run after decision edges
      }
      

      
      // Trigger repositioning after state update (but NOT for decision node children)
      if (shouldReorganize && !isAddingToDecisionNode) {
        setShouldRepositionAfterNodeAddition(true);
      }
    }

    // Reset form state
    resetModalState();
  }, [
    modalState,
    hasChildWithIdentifier,
    edges,
    nodes,
    placeholders,
    globalVariableRegistry,
    setGlobalVariableRegistry,
    saveToHistory,
    isFirstNode,
    setNodes,
    setIsFirstNode,
    setShouldRepositionAfterNodeAddition,
    identifiers,
    setIdentifiers,
    setEdges,
    resetModalState,
    updateNodeHandlers,
    repositionChildrenCentered
  ]);

  const handleCancel = useCallback(() => {
    resetModalState();
    setPlaceholders({});
  }, [resetModalState, setPlaceholders]);

  // Helper function to clean up decision paths when nodes are deleted
  const cleanupDecisionPaths = useCallback((deletedNodeIds: string[], deletedIdentifiers: string[]) => {
    setNodes(currentNodes => 
      currentNodes.map(node => {
        if (node.data.entityType === "DECISION" && node.data.decisionData) {
          const decisionData = { ...node.data.decisionData };
          let pathsChanged = false;
          
          // Check if truePath references a deleted node (by identifier or node ID)
          if (decisionData.truePath) {
            const isDeletedByIdentifier = deletedIdentifiers.includes(decisionData.truePath);
            const isDeletedByNodeId = deletedNodeIds.includes(decisionData.truePath);
            if (isDeletedByIdentifier || isDeletedByNodeId) {
              decisionData.truePath = null;
              pathsChanged = true;
            }
          }
          
          // Check if falsePath references a deleted node (by identifier or node ID)
          if (decisionData.falsePath) {
            const isDeletedByIdentifier = deletedIdentifiers.includes(decisionData.falsePath);
            const isDeletedByNodeId = deletedNodeIds.includes(decisionData.falsePath);
            if (isDeletedByIdentifier || isDeletedByNodeId) {
              decisionData.falsePath = null;
              pathsChanged = true;
            }
          }
          
          // Return updated node if paths changed
          if (pathsChanged) {
            return {
              ...node,
              data: {
                ...node.data,
                decisionData
              }
            };
          }
        }
        return node;
      })
    );
  }, [setNodes]);

  // Delete a single node
  const deleteNode = useCallback((nodeId: string) => {
    // Find the node
    const nodeToDelete = nodes.find(node => node.id === nodeId);
    if (!nodeToDelete) return;
    
    // Save current state to history
    saveToHistory("delete");
    
    // Check if this is the last node
    const isLastNode = nodes.length === 1;
    
    if (isLastNode) {
      // For the last node, set a clean initial state instead of filtering
      setTimeout(() => {
        setNodes(initialNodes);
        setEdges(initialEdges);
        setIsFirstNode(true);
        
        message.success("Conversation Deleted");
      }, 50);
    } else {
      // Clean up decision paths that reference the deleted node
      const deletedIdentifier = nodeToDelete.data.identifier;
      cleanupDecisionPaths([nodeId], deletedIdentifier ? [deletedIdentifier] : []);
      
      // Delete the node from nodes array
      setNodes(nodes => nodes.filter(node => node.id !== nodeId));
      
      // Delete any edges connecting to this node
      setEdges(edges => edges.filter(edge => 
        edge.source !== nodeId && edge.target !== nodeId
      ));
      
      // Trigger repositioning via the state flag to ensure it happens after state updates
      setShouldRepositionAfterNodeAddition(true);
      
      message.success("Node deleted");
    }
  }, [nodes, saveToHistory, setNodes, setEdges, setIsFirstNode, setShouldRepositionAfterNodeAddition, initialNodes, initialEdges, cleanupDecisionPaths]);

  // Delete a node and all its descendants (branch)
  const deleteBranch = useCallback((nodeId: string) => {
    // Find all descendants
    const descendantIds = getNodeDescendants(nodeId, nodes, edges);
    if (!descendantIds.length) {
      // If no descendants, fallback to single node delete
      deleteNode(nodeId);
      return;
    }
    
    // Save current state to history
    saveToHistory("delete");
    
    // Include the root node of the branch
    const nodesToDelete = [nodeId, ...descendantIds];
    
    // Check if we're deleting all nodes
    const isLastBranch = nodes.length === nodesToDelete.length;
    
    if (isLastBranch) {
      // For the last branch, set a clean initial state instead of filtering
      setTimeout(() => {
        setNodes(initialNodes);
        setEdges(initialEdges);
        setIsFirstNode(true);
        
        message.success("Tree deleted");
      }, 50);
    } else {
      // Collect identifiers from all nodes being deleted
      const deletedIdentifiers = nodesToDelete
        .map(id => nodes.find(n => n.id === id)?.data.identifier)
        .filter(identifier => identifier) as string[];
      
      // Clean up decision paths that reference any of the deleted nodes
      cleanupDecisionPaths(nodesToDelete, deletedIdentifiers);
      
      // Delete all nodes in the branch
      setNodes(nodes => nodes.filter(node => !nodesToDelete.includes(node.id)));
      
      // Delete all edges connected to any of these nodes
      setEdges(edges => edges.filter(edge => 
        !nodesToDelete.includes(edge.source) && !nodesToDelete.includes(edge.target)
      ));
      
      // Trigger repositioning via the state flag to ensure it happens after state updates
      setShouldRepositionAfterNodeAddition(true);
      
      message.success(`Branch with ${nodesToDelete.length} nodes deleted`);
    }
  }, [nodes, edges, getNodeDescendants, deleteNode, saveToHistory, setNodes, setEdges, setIsFirstNode, setShouldRepositionAfterNodeAddition, initialNodes, initialEdges, cleanupDecisionPaths]);

  // Show delete node confirmation
  const showDeleteNodeConfirmation = useCallback((nodeId: string) => {
    // This function will be implemented in the main component since it uses setDeleteModalState
    // We'll return a function that can be called with the modal state setters
    return nodeId;
  }, []);

  // Show delete branch confirmation
  const showDeleteBranchConfirmation = useCallback((nodeId: string) => {
    // This function will be implemented in the main component since it uses setDeleteModalState
    // We'll return a function that can be called with the modal state setters
    return nodeId;
  }, []);

  // Handle delete node confirmation
  const handleDeleteNodeConfirm = useCallback(() => {
    if (!deleteModalState.nodeToDeleteFromMenu) return;
    
    if (deleteModalState.deleteChildren) {
      // Delete the node and all its children (branch)
      deleteBranch(deleteModalState.nodeToDeleteFromMenu);
    } else {
      // Delete just the single node
      deleteNode(deleteModalState.nodeToDeleteFromMenu);
    }
    
    resetDeleteModalState();
  }, [deleteModalState.nodeToDeleteFromMenu, deleteModalState.deleteChildren, deleteBranch, deleteNode, resetDeleteModalState]);

  // Handle delete branch confirmation
  const handleDeleteBranchConfirm = useCallback(() => {
    if (!deleteModalState.nodeToDeleteFromMenu) return;
    deleteBranch(deleteModalState.nodeToDeleteFromMenu);
    resetDeleteModalState();
  }, [deleteModalState.nodeToDeleteFromMenu, deleteBranch, resetDeleteModalState]);

  // Handle delete cancellation
  const handleDeleteModalCancel = useCallback(() => {
    resetDeleteModalState();
  }, [resetDeleteModalState]);

  // Custom reorganization function that uses our centering logic instead of ELK
  const customReorganizeTree = useCallback(() => {
    console.log(`[DEBUG] Custom reorganization starting - using improved algorithm`);
    
    if (nodes.length === 0) {
      console.log(`[DEBUG] No nodes available for reorganization`);
      return;
    }

    // Filter out placeholder nodes
    const realNodes = nodes.filter(node => !node.data.isPlaceholder);
    if (realNodes.length === 0) {
      console.log(`[DEBUG] No real nodes available for reorganization`);
      return;
    }
    
    // Find the start node using the specified priority:
    // 1. Node marked as starting node (isStartNode)
    // 2. Node marked as potential start (potentialStart) 
    // 3. Node that has no parent nodes (root node)
    // 4. Node with the lowest node ID
    let startNode: Node | null = null;
    
    // Priority 1: Look for nodes marked with isStartNode
    startNode = realNodes.find(node => node.data.isStartNode) || null;
    if (startNode) {
      console.log(`[DEBUG] Found start node marked with isStartNode: ${startNode.id}`);
    }
    
    // Priority 2: Look for nodes marked with potentialStart
    if (!startNode) {
      startNode = realNodes.find(node => node.data.potentialStart) || null;
      if (startNode) {
        console.log(`[DEBUG] Found start node marked with potentialStart: ${startNode.id}`);
      }
    }
    
    // Priority 3: Look for root nodes (no incoming edges)
    if (!startNode) {
      const rootNodes = realNodes.filter(node => 
        !edges.some(edge => edge.target === node.id)
      );
      if (rootNodes.length > 0) {
        startNode = rootNodes[0];
        console.log(`[DEBUG] Found root node (no parents): ${startNode.id}`);
      }
    }
    
    // Priority 4: Use node with lowest ID
    if (!startNode) {
      startNode = realNodes.reduce((lowest, node) => {
        const lowestId = parseInt(lowest.id, 10) || Infinity;
        const nodeId = parseInt(node.id, 10) || Infinity;
        return nodeId < lowestId ? node : lowest;
      });
      console.log(`[DEBUG] Using node with lowest ID as start: ${startNode.id}`);
    }
    
    if (!startNode) {
      console.log(`[DEBUG] Could not determine start node`);
      return;
    }
    
    // Position the start node at a standard location
    const startX = 250;
    const startY = 100;
    
    console.log(`[DEBUG] Positioning start node ${startNode.id} at (${startX}, ${startY})`);
    
    // Update the start node position
    setNodes(currentNodes => {
      return currentNodes.map(node => {
        if (node.id === startNode!.id) {
          return {
            ...node,
            position: { x: startX, y: startY }
          };
        }
        return node;
      });
    });
    
    // Now traverse the tree from the start node and reposition children recursively
    setTimeout(() => {
      const positionedNodes = new Set<string>(); // Track nodes that have been positioned (first parent wins)
      const repositionSubtree = (nodeId: string, currentPath: Set<string> = new Set()) => {
        // Check for cycles in the current path only (not globally)
        if (currentPath.has(nodeId)) {
          console.log(`[DEBUG] Cycle detected at node ${nodeId} in current path [${Array.from(currentPath).join(' -> ')}], stopping this branch`);
          return;
        }
        
        // Add current node to the path for this traversal branch
        const newPath = new Set(currentPath);
        newPath.add(nodeId);
        
        const currentNode = nodes.find(n => n.id === nodeId);
        if (!currentNode) {
          console.log(`[DEBUG] Node ${nodeId} not found, skipping`);
          return;
        }
        
        // Get direct children of this node
        let childIds = edges
          .filter(edge => edge.source === nodeId)
          .map(edge => edge.target);
        
        // For decision nodes, also include nodes referenced by decision paths
        if (currentNode.data.entityType === "DECISION" && currentNode.data.decisionData) {
          const decisionData = currentNode.data.decisionData;
          const decisionChildren = nodes
            .filter(n => {
              const nodeIdentifier = n.data.identifier || n.id;
              return nodeIdentifier === decisionData.truePath || nodeIdentifier === decisionData.falsePath;
            })
            .map(n => n.id);
          
          // Combine edge children and decision children, removing duplicates
          const combinedChildren = childIds.concat(decisionChildren);
          childIds = combinedChildren.filter((id, index) => combinedChildren.indexOf(id) === index);
          console.log(`[DEBUG] Decision node ${nodeId} has edge children: [${edges.filter(e => e.source === nodeId).map(e => e.target).join(', ')}] and decision children: [${decisionChildren.join(', ')}]`);
        }
        
        if (childIds.length === 0) {
          console.log(`[DEBUG] Node ${nodeId} has no children, ending traversal branch`);
          return;
        }
        
        console.log(`[DEBUG] Repositioning children of node ${nodeId}: [${childIds.join(', ')}]`);
        
        // Apply the repositioning logic to this node's children
        setNodes(currentNodes => {
          let result = currentNodes;
          
          // Check if this is a decision node - needs special positioning
          if (currentNode.data.entityType === "DECISION") {
            console.log(`[DEBUG] Applying decision node repositioning for ${nodeId}`);
            
            // For decision nodes, manually position only unprocessed nodes
            if (currentNode.data.decisionData) {
              // Get dynamic dimensions for the decision node
              const decisionDimensions = getNodeDimensions(nodeId);
              
              result = currentNodes.map(node => {
                // Skip the decision node itself
                if (node.id === nodeId) return node;
                
                // Check if this node is linked to the decision node
                const nodeIdentifier = node.data.identifier || node.id;
                const isTruePath = currentNode.data.decisionData.truePath === nodeIdentifier;
                const isFalsePath = currentNode.data.decisionData.falsePath === nodeIdentifier;
                
                if (!isTruePath && !isFalsePath) return node;
                
                // Check if this node has already been processed in our current path
                if (newPath.has(node.id)) {
                  console.log(`[DEBUG] Decision path node ${node.id} already processed in current traversal, skipping repositioning`);
                  return node;
                }
                
                // Check if this node has already been positioned by another parent
                if (positionedNodes.has(node.id)) {
                  console.log(`[DEBUG] Decision path node ${node.id} already positioned by another parent, skipping repositioning`);
                  return node;
                }
                
                // Position the node relative to the decision node using dynamic spacing
                const xOffset = isTruePath ? -decisionDimensions.horizontalSpacing : decisionDimensions.horizontalSpacing;
                const newPosition = {
                  x: currentNode.position.x + xOffset,
                  y: currentNode.position.y + decisionDimensions.verticalSpacing
                };
                
                console.log(`[DEBUG] Repositioning decision ${isTruePath ? 'true' : 'false'} path node ${node.id} to ${JSON.stringify(newPosition)} using dynamic spacing (h:${decisionDimensions.horizontalSpacing}, v:${decisionDimensions.verticalSpacing})`);
                
                // Mark this node as positioned
                positionedNodes.add(node.id);
                
                return {
                  ...node,
                  position: newPosition
                };
              });
            }
          } else {
            // For regular nodes, use the standard centering logic with positioned nodes tracking
            result = repositionChildrenCentered(nodeId, currentNodes, edges, positionedNodes);
          }
          
          // Continue with each child after a short delay
          // Each child gets its own path copy to allow independent traversal
          setTimeout(() => {
            childIds.forEach(childId => {
              repositionSubtree(childId, newPath);
            });
          }, 50);
          
          return result;
        });
      };
      
      // Start the recursive repositioning from the start node
      repositionSubtree(startNode.id);
      
    }, 100);
    
  }, [nodes, edges, setNodes, repositionChildrenCentered]);

  return {
    handleOk,
    handleCancel,
    deleteNode,
    deleteBranch,
    showDeleteNodeConfirmation,
    showDeleteBranchConfirmation,
    handleDeleteNodeConfirm,
    handleDeleteBranchConfirm,
    handleDeleteModalCancel,
    calculateSmartPosition,
    repositionChildrenCentered,
    customReorganizeTree
  };
}; 